import { useAsync, UseAsyncStatus } from '@shared/async';
import { useErrorHandler } from '@shared/errors';
import { useHeaderDetails } from '@shared/header';
import { maxLength, required } from '@shared/validators';
import { isValidDate } from '@shared/validators/date-time-validators';
import { Basket } from 'grommet-icons';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { DateTextInput, NumericTextInput } from '@/components/form-controls';
import { Button } from '@/components-new/button';
import { Combobox } from '@/components-new/combobox';
import { ErrorMessage, Field, Label } from '@/components-new/fieldset';
import { Input } from '@/components-new/input';
import { Radio, RadioField, RadioGroup } from '@/components-new/radio';
import { Select } from '@/components-new/select';
import { useMarketBasketService } from '@/features/market-baskets';
import { DrugLookupSelect } from '@/features/market-baskets/components/drug-lookup-select';
import { useCreateMarketBasket } from '@/features/market-baskets/create-market-basket.provider';
import { useMarketBaskets } from '@/features/market-baskets/market-baskets.provider';
import {
  CreateMarketBasketFormInputs,
  defaultCreateMarketBasketForm,
} from '@/features/market-baskets/types/market-basket-form-inputs';
import { notifySuccess } from '@/lib/notification/notifications';
import { Lookup } from '@/types/lookup';

type CreationMode = 'new' | 'clone';

const NewMarketBasketPage = () => {
  const { searchClients, clientLookups } = useCreateMarketBasket();
  const { marketBaskets, loadMarketBaskets } = useMarketBaskets();
  const { handleError } = useErrorHandler();
  const { setHeaderDetails, clearHeaderDetails } = useHeaderDetails();
  const { createMarketBasket, uploadMarketDetailsFile, cloneMarketBasket } =
    useMarketBasketService();
  const navigate = useNavigate();

  const [mode, setMode] = useState<CreationMode>('new');
  const [cloneSource, setCloneSource] = useState<Lookup | undefined>(undefined);
  const [cloneSourceQuery, setCloneSourceQuery] = useState<string | undefined>(
    undefined,
  );
  const [cloneName, setCloneName] = useState('');
  const [cloneNameError, setCloneNameError] = useState<string | undefined>(
    undefined,
  );

  const {
    control,
    formState: { errors, isValid },
    handleSubmit,
    reset,
    watch,
  } = useForm<CreateMarketBasketFormInputs>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: defaultCreateMarketBasketForm,
  });

  const clientValue = watch('client');
  const purposeValue = watch('purpose');

  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(
    null,
  );

  useEffect(() => {
    setHeaderDetails({
      documentTitle: 'Add Market Basket',
      pageTitle: 'Add Market Basket',
      breadcrumbs: [
        { icon: Basket, label: 'Market Baskets', url: '/market-baskets' },
        { label: 'Add Market Basket', url: '/market-baskets/new' },
      ],
    });
    return () => {
      clearHeaderDetails();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadMarketBaskets();
    return () => {
      reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCreate = async (form: CreateMarketBasketFormInputs) => {
    const { id } = await createMarketBasket(form);
    const marketDetailsFile = form.marketDetailsFile;

    if (!marketDetailsFile) {
      reset();
      notifySuccess({
        title: 'Created Successfully',
        message: 'Successfully created market basket.',
      });
      navigate(`/market-baskets/${id}`);
      return;
    }

    try {
      await uploadMarketDetailsFile(marketDetailsFile, id);
      notifySuccess({
        title: 'Created Successfully',
        message: 'Successfully created market basket.',
      });
      reset();
    } catch (marketDetailsFileError) {
      handleError(
        marketDetailsFileError as Error,
        {
          title: 'Gold Standard Upload Failed',
          message:
            'Market Basket creation was successful, but the Gold Standard file could not be parsed. Please try importing the data again.',
        },
        'warn',
      );
    } finally {
      navigate(`/market-baskets/${id}`);
    }
  };

  const onClone = async () => {
    if (!cloneSource) return;
    if (!cloneName.trim()) {
      setCloneNameError('Name is required');
      return;
    }
    if (cloneName.length > 100) {
      setCloneNameError('Name cannot exceed 100 characters');
      return;
    }
    setCloneNameError(undefined);
    try {
      const { id } = await cloneMarketBasket(
        cloneSource.id as number,
        cloneName.trim(),
      );
      notifySuccess({
        title: 'Cloned Successfully',
        message: `"${cloneName}" has been created with all packagings from "${cloneSource.label}".`,
      });
      navigate(`/market-baskets/${id}`);
    } catch (ex) {
      handleError(ex as Error, {
        title: 'Clone Failed',
        message:
          'Unable to clone market basket. The name may already be in use.',
      });
    }
  };

  const onCreateAsync = useAsync(onCreate);
  const onCloneAsync = useAsync(onClone);
  const saving =
    onCreateAsync.status === UseAsyncStatus.Pending ||
    onCloneAsync.status === UseAsyncStatus.Pending;

  useEffect(() => {
    const { status, error } = onCreateAsync;
    if (status === UseAsyncStatus.Pending || status === UseAsyncStatus.Idle)
      return;
    if (status === UseAsyncStatus.Error) {
      handleError(error, {
        title: 'Creation Failed',
        message:
          'We encountered an unexpected error while creating the market basket. Please try again or contact an administrator.',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onCreateAsync.status]);

  const handleCancel = () => navigate('..');

  const delaySearch = (searchCallback: () => void) => {
    if (searchTimeout) clearTimeout(searchTimeout);
    setSearchTimeout(setTimeout(searchCallback, 100));
  };

  const basketLookups: Lookup[] = marketBaskets
    .filter((mb) => !mb.archivedAt)
    .filter(
      (mb) =>
        !cloneSourceQuery ||
        mb.name.toLowerCase().includes(cloneSourceQuery.toLowerCase()),
    )
    .map((mb) => ({ id: mb.id, label: mb.name }));

  const purposeOptions = [
    { label: 'Market Share', value: 'market_share' },
    { label: 'Bid Analysis', value: 'bid_analysis' },
  ];

  return (
    <div className="flex justify-center px-4 py-8">
      <div className="w-full max-w-2xl rounded-xl bg-white p-8 shadow-sm ring-1 ring-zinc-200">
        {/* Mode selector */}
        <div className="mb-6">
          <Field>
            <Label>How would you like to create this market basket?</Label>
            <RadioGroup
              value={mode}
              onChange={(v) => setMode(v as CreationMode)}
            >
              <RadioField>
                <Radio value="new" />
                <Label>Start fresh</Label>
              </RadioField>
              <RadioField>
                <Radio value="clone" />
                <Label>
                  Clone an existing basket — copies all packagings and pricing
                </Label>
              </RadioField>
            </RadioGroup>
          </Field>
        </div>

        {mode === 'clone' ? (
          <div className="space-y-4">
            <Field>
              <Label>
                Source basket <span className="text-red-500">*</span>
              </Label>
              <Combobox
                options={basketLookups}
                value={cloneSource}
                onChange={setCloneSource}
                onQueryChange={setCloneSourceQuery}
                valueKey="id"
                labelKey="label"
                placeholder="Search for a basket to clone…"
              />
            </Field>
            <Field>
              <Label>
                New name <span className="text-red-500">*</span>
              </Label>
              <Input
                value={cloneName}
                onChange={(e) => {
                  setCloneName(e.target.value);
                  setCloneNameError(undefined);
                }}
                placeholder={
                  cloneSource
                    ? `${cloneSource.label} (Copy)`
                    : 'New basket name'
                }
                invalid={!!cloneNameError}
              />
              {cloneNameError && <ErrorMessage>{cloneNameError}</ErrorMessage>}
            </Field>
            <div className="flex justify-end gap-3 pt-2">
              <Button outline onClick={handleCancel} disabled={saving}>
                Cancel
              </Button>
              <Button
                onClick={() => void onCloneAsync.execute()}
                disabled={saving || !cloneSource || !cloneName.trim()}
              >
                {saving ? 'Cloning…' : 'Clone Basket'}
              </Button>
            </div>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit((v) => void onCreateAsync.execute(v))}
            className="space-y-4"
          >
            <Controller
              control={control}
              name="name"
              rules={{
                validate: {
                  maxLength: maxLength(100),
                  required: required('Name is required'),
                },
              }}
              render={({ field }) => (
                <Field>
                  <Label>
                    Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    {...field}
                    placeholder="Market Basket Name"
                    invalid={!!errors.name}
                  />
                  {errors.name?.message && (
                    <ErrorMessage>{errors.name.message}</ErrorMessage>
                  )}
                </Field>
              )}
            />

            <Controller
              control={control}
              name="client"
              rules={{ validate: { required: required('Client is required') } }}
              render={({ field: { value, onChange, onBlur } }) => (
                <Field>
                  <Label>
                    Client <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={(value as Lookup)?.id?.toString() ?? ''}
                    onChange={(e) => {
                      const selected = clientLookups.find(
                        (c) => c.id.toString() === e.target.value,
                      );
                      onChange(selected ?? null);
                    }}
                    onBlur={onBlur}
                    onFocus={() => delaySearch(() => searchClients(''))}
                  >
                    <option value="">Search for Clients</option>
                    {clientLookups.map((c) => (
                      <option key={c.id} value={c.id.toString()}>
                        {c.label}
                      </option>
                    ))}
                  </Select>
                  {errors.client?.message && (
                    <ErrorMessage>{errors.client.message}</ErrorMessage>
                  )}
                </Field>
              )}
            />

            <Controller
              control={control}
              name="purpose"
              rules={{
                validate: { required: required('Purpose is required') },
              }}
              render={({ field: { value, onChange, onBlur } }) => (
                <Field>
                  <Label>
                    Purpose <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onBlur={onBlur}
                  >
                    <option value="">Select Purpose</option>
                    {purposeOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Select>
                  {errors.purpose?.message && (
                    <ErrorMessage>{errors.purpose.message}</ErrorMessage>
                  )}
                </Field>
              )}
            />

            {purposeValue === 'market_share' && clientValue && (
              <Controller
                control={control}
                name="drug"
                rules={{ validate: { required: required('Drug is required') } }}
                render={({ field: { value, onChange, onBlur } }) => (
                  <Field>
                    <Label>
                      Drug <span className="text-red-500">*</span>
                    </Label>
                    <DrugLookupSelect
                      key={(clientValue as Lookup)?.id || 'no-client'}
                      name="drug"
                      value={value ?? undefined}
                      onChange={onChange}
                      onBlur={onBlur}
                      clear={{ label: 'Clear Drug' }}
                    />
                    {errors.drug?.message && (
                      <ErrorMessage>{errors.drug.message}</ErrorMessage>
                    )}
                  </Field>
                )}
              />
            )}

            <div className="grid grid-cols-2 gap-4">
              <Controller
                control={control}
                name="currentCpiu"
                rules={{
                  validate: { required: required('Current CPI-U is required') },
                }}
                render={({ field: { value, onChange, onBlur } }) => (
                  <Field>
                    <Label>
                      Current CPI-U{' '}
                      <a
                        href="https://www.bls.gov/regions/mid-atlantic/data/consumerpriceindexhistorical_us_table.htm"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-secondary-600 underline hover:text-secondary-500"
                      >
                        (BLS.gov)
                      </a>{' '}
                      <span className="text-red-500">*</span>
                    </Label>
                    <NumericTextInput
                      onBlur={onBlur}
                      onChange={onChange}
                      value={value}
                      maxWholePlaces={3}
                      maxDecimalPlaces={3}
                      placeholder="123.456"
                    />
                    {errors.currentCpiu?.message && (
                      <ErrorMessage>{errors.currentCpiu.message}</ErrorMessage>
                    )}
                  </Field>
                )}
              />
              <Controller
                control={control}
                name="currentCpiuDate"
                rules={{
                  validate: {
                    required: required('Current CPI-U Date is required'),
                    valid: isValidDate,
                  },
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <Field>
                    <Label>
                      Current CPI-U Date <span className="text-red-500">*</span>
                    </Label>
                    <DateTextInput
                      value={value}
                      onChange={(e) =>
                        onChange((e.target as HTMLInputElement).value)
                      }
                      onBlur={onBlur}
                    />
                    {errors.currentCpiuDate?.message && (
                      <ErrorMessage>
                        {errors.currentCpiuDate.message}
                      </ErrorMessage>
                    )}
                  </Field>
                )}
              />
            </div>

            <Controller
              control={control}
              name="marketDetailsFile"
              render={({ field: { onChange, value } }) => (
                <div>
                  <span className="text-sm font-medium text-zinc-700">
                    Gold Standard File (Optional)
                  </span>
                  <div className="mt-2 flex justify-center rounded-lg border border-dashed border-gray-900/25 px-6 py-10">
                    <div className="text-center text-sm text-gray-600">
                      <label className="cursor-pointer font-semibold text-secondary-600 hover:text-secondary-500">
                        {(value as File | null)?.name ?? 'Upload a file'}
                        <input
                          type="file"
                          accept=".csv"
                          style={{
                            position: 'fixed',
                            opacity: 0,
                            width: 1,
                            height: 1,
                          }}
                          onChange={(e) =>
                            onChange(
                              (e.target.files?.length ?? 0) > 0
                                ? e.target.files![0]
                                : null,
                            )
                          }
                        />
                      </label>
                    </div>
                  </div>
                </div>
              )}
            />

            <div className="flex justify-end gap-3 pt-2">
              <Button
                outline
                type="button"
                onClick={handleCancel}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={!isValid || saving}>
                {saving ? 'Creating…' : 'Create'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default NewMarketBasketPage;
